import React from "react";
import PropTypes from "prop-types";
import Card from "shared/Card";
import Button, { ButtonPurpose } from "shared/Button";
import Checkbox from "shared/@forms/Checkbox";
import { formatCurrency } from "shared/@utils/money";
import ProductItemCard from "../../../Store/components/ProductItemCard";
import styles from "./OrderCard.module.css";

// Map an order line item to the shape ProductItemCard expects so the order
// details / cart rows render with the exact same card as the Store catalog.
// The catalog's Type column slot carries the price paid and the Status column
// slot carries the quantity (see the header labels below).
const toCardProduct = (item, qtyNode) => ({
  id: item.id,
  image: item.image,
  name: item.name,
  date: item.date,
  retailPrice: item.retailPrice,
  pm: item.pm,
  author: item.author,
  sku: item.sku,
  variationLabel: item.variationLabel,
  // Was `item.variation`, which no producer ever emitted, so the colour/variation
  // row silently never rendered.
  variationValue: item.variationValue,
  size: item.size,
  productType: formatCurrency(item.price),
  statusLabel: qtyNode,
  pinLevels: [],
  memberships: [],
  tags: [],
});

export function OrderCard({
  items = [],
  headerAction = null,
  cancelledBanner = null,
  actionColWidth = "180px",
  selectable = false,
  onRemoveSelected = null,
  bulkActions = null,
}) {
  const keyForItem = React.useCallback(
    (item, idx) => (item.id !== undefined && item.id !== null ? item.id : idx),
    []
  );

  // Only items that opt in (item.selectable !== false) can be checked/removed.
  // This lets a cart mix read-only rows (the IBO's own items) with editable
  // pending rows (the rep's not-yet-submitted additions).
  const selectableKeys = React.useMemo(
    () =>
      items
        .map((item, idx) => ({ item, key: keyForItem(item, idx) }))
        .filter(({ item }) => item.selectable !== false)
        .map(({ key }) => key),
    [items, keyForItem]
  );

  const [selectedKeys, setSelectedKeys] = React.useState(() => new Set());

  // Drop any selections whose rows no longer exist (e.g. after a removal).
  React.useEffect(() => {
    setSelectedKeys((prev) => {
      const next = new Set();
      selectableKeys.forEach((key) => {
        if (prev.has(key)) {
          next.add(key);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [selectableKeys]);

  const allSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every((key) => selectedKeys.has(key));

  const toggleSelect = React.useCallback((key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = React.useCallback(() => {
    setSelectedKeys((prev) =>
      prev.size === selectableKeys.length ? new Set() : new Set(selectableKeys)
    );
  }, [selectableKeys]);

  const handleRemoveSelected = React.useCallback(() => {
    if (!onRemoveSelected || selectedKeys.size === 0) {
      return;
    }
    onRemoveSelected(Array.from(selectedKeys));
    setSelectedKeys(new Set());
  }, [onRemoveSelected, selectedKeys]);

  // The column header and every row scroll horizontally on their own once the
  // grid stops fitting the card. Left alone they would drift apart and the
  // labels would stop describing the values underneath them, so mirror one
  // scroll position across all of them.
  const headerScrollRef = React.useRef(null);
  const rowScrollRefs = React.useRef(new Set());
  const syncingRef = React.useRef(false);

  const registerRowScroll = React.useCallback((el) => {
    if (el) rowScrollRefs.current.add(el);
  }, []);

  const handleScrollSync = React.useCallback((event) => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    const { scrollLeft } = event.currentTarget;
    const refs = rowScrollRefs.current;
    // React 18 ignores cleanup functions returned from ref callbacks and calls
    // them with null on unmount, so there is no hook to remove a specific row.
    // Drop anything that has left the document instead, otherwise switching
    // orders leaks a detached node per row.
    refs.forEach((el) => {
      if (!el.isConnected) refs.delete(el);
    });
    [headerScrollRef.current, ...refs].forEach((el) => {
      if (el && el !== event.currentTarget && el.scrollLeft !== scrollLeft) {
        el.scrollLeft = scrollLeft;
      }
    });
    // Released on the next frame so the scrollLeft writes above, which fire
    // their own scroll events, don't recurse.
    window.requestAnimationFrame(() => {
      syncingRef.current = false;
    });
  }, []);

  // The per-row action column is a fixed track, so it reserves its width even
  // when no row renders a button into it. On an order whose items have no
  // Re-Ship / Cancel action that was 180px of empty column pushing the row past
  // the width the panel gets, which showed as a permanent horizontal scrollbar.
  // Collapse the track when nothing will be drawn in it.
  const hasRowActions = (items || []).some(
    (it) => it && it.showButtons && (it.secondaryBtnText || it.primaryBtnText)
  );
  const effectiveActionColWidth = hasRowActions ? actionColWidth : "0px";

  return (
    <Card
      className={styles.card}
      style={{ "--action-col-width": effectiveActionColWidth }}
    >
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>Items In Order</h3>
          {headerAction}
        </div>
        {selectable && selectableKeys.length > 0 && (
          <div className={styles.selectBar}>
            <Button
              type="button"
              purpose={ButtonPurpose.generic}
              className={styles.selectAllButton}
              title={
                allSelected
                  ? "Clear the selection of all items."
                  : "Select all selectable items in this order."
              }
              onClick={toggleSelectAll}
            >
              {allSelected ? "Unselect All" : "Select All"}
            </Button>
            {bulkActions
              ? bulkActions.map((action) => (
                  <Button
                    key={action.key}
                    type="button"
                    purpose={action.purpose ?? ButtonPurpose.delete}
                    className={styles.removeSelectedButton}
                    title={`${action.label} the selected items.`}
                    onClick={() => action.onClick(Array.from(selectedKeys))}
                    disabled={selectedKeys.size === 0}
                  >
                    {action.label} ({selectedKeys.size})
                  </Button>
                ))
              : onRemoveSelected && (
                  <Button
                    type="button"
                    purpose={ButtonPurpose.delete}
                    className={styles.removeSelectedButton}
                    title="Remove the selected items from the order."
                    onClick={handleRemoveSelected}
                    disabled={selectedKeys.size === 0}
                  >
                    Remove Selected ({selectedKeys.size})
                  </Button>
                )}
          </div>
        )}
        <div
          className={`${styles.columnsScroll}${
            selectable ? " " + styles.columnsScrollSelectable : ""
          }`}
          ref={headerScrollRef}
          onScroll={handleScrollSync}
        >
          <div
            className={styles.columns}
            style={{ "--action-col-width": effectiveActionColWidth }}
          >
            <span>Image</span>
            <span>Name</span>
            <span title="Stock Keeping Unit">SKU:</span>
            <span>Price:</span>
            <span>Quantity:</span>
            <span aria-hidden />
          </div>
        </div>
      </header>

      {cancelledBanner && (
        <div className={styles.cancelledBanner}>{cancelledBanner}</div>
      )}

      <div className={styles.list}>
        {items.map((item, idx) => {
          const qtyNode = item.onQtyChange ? (
            <input
              type="number"
              min="1"
              step="1"
              className={styles.qtyInput}
              value={item.qty ?? 1}
              onChange={(event) => item.onQtyChange(event.target.value)}
            />
          ) : item.qty !== undefined && item.qty !== null && item.qty !== "" ? (
            item.qty
          ) : null;

          const actions =
            item.showButtons &&
            (item.secondaryBtnText || item.primaryBtnText) ? (
              <div className={styles.actions}>
                {item.secondaryBtnText && (
                  <Button
                    type="button"
                    purpose={ButtonPurpose.generic}
                    className={styles.secondaryButton}
                    title={`${item.secondaryBtnText} this item.`}
                    onClick={item.onSecondaryClick}
                  >
                    {item.secondaryBtnText}
                  </Button>
                )}
                {item.primaryBtnText && (
                  <Button
                    type="button"
                    purpose={ButtonPurpose.delete}
                    className={styles.primaryButton}
                    title={`${item.primaryBtnText} this item.`}
                    onClick={item.onPrimaryClick}
                  >
                    {item.primaryBtnText}
                  </Button>
                )}
              </div>
            ) : null;

          const imageBadge = item.wasOverridden ? (
            <span
              className={`${styles.rowBadge} ${styles.adminOverrideTag}`}
              title="Admin Override — added product outside of audience permissions"
            >
              Admin Override
            </span>
          ) : item.pending ? (
            <span
              className={`${styles.rowBadge} ${styles.pendingTag}`}
              title="Pending — not yet submitted to the IBO's cart"
            >
              Pending
            </span>
          ) : item.addedByAdmin ? (
            <span
              className={styles.rowBadge}
              title="This item was added by an admin, not the IBO"
            >
              Added by Admin
            </span>
          ) : null;

          return (
            <div
              className={`${styles.itemRow}${
                selectable ? " " + styles.itemRowSelectable : ""
              }${item.cancelled ? " " + styles.itemRowCancelled : ""}${
                item.fullyRefunded ? " " + styles.itemRowRefunded : ""
              }`}
              key={keyForItem(item, idx)}
            >
              {item.cancelled && (
                <span className={styles.cancelledTag}>Cancelled</span>
              )}
              {!item.cancelled && item.refunded && (
                <span className={styles.refundedTag}>
                  {item.fullyRefunded
                    ? "Refunded"
                    : `Refunded ${item.refundedQty}/${item.qty}`}
                </span>
              )}
              <div
                className={styles.rowBody}
                ref={registerRowScroll}
                onScroll={handleScrollSync}
              >
                <ProductItemCard
                  product={toCardProduct(item, qtyNode)}
                  actionColWidth={effectiveActionColWidth}
                  imageBadge={imageBadge}
                  showEmptyTagDivider={false}
                >
                  {actions}
                </ProductItemCard>
              </div>
              {selectable && item.selectable !== false && (
                <span className={styles.rowCheckbox}>
                  <Checkbox
                    checked={selectedKeys.has(keyForItem(item, idx))}
                    onChange={() => toggleSelect(keyForItem(item, idx))}
                    aria-label={`Select ${item.name || "item"}`}
                  />
                </span>
              )}
              {selectable && item.selectable === false && (
                <span className={styles.checkboxSpacer} aria-hidden />
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

OrderCard.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  headerAction: PropTypes.node,
  cancelledBanner: PropTypes.string,
  actionColWidth: PropTypes.string,
  selectable: PropTypes.bool,
  onRemoveSelected: PropTypes.func,
  bulkActions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      purpose: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    })
  ),
};

export default OrderCard;
